import { getFactionAccentColorForLogEntity } from './factionUiColors.js';

/**
 * Localized action history text: full verbatim sentences (names and zones in text,
 * not implied by thumbnails).
 */

/**
 * @param {*} playerId
 * @param {{ userId?: *, opponentId?: *, currentUsername?: string, opponentUsername?: string } | null | undefined} ctx
 * @param {import('i18next').TFunction} t
 */
export function resolveActionHistoryPlayerName(playerId, ctx, t) {
  if (playerId == null || !ctx) return '';
  if (String(playerId) === String(ctx.userId)) return ctx.currentUsername || String(playerId);
  if (ctx.opponentId != null && String(playerId) === String(ctx.opponentId)) {
    return ctx.opponentUsername || String(playerId);
  }
  if (playerId === 'bot' || playerId === 'campaign_bot') {
    return t('battleUI.actionHistory.botPlayerName');
  }
  return String(playerId);
}

function primaryEntityName(entry) {
  const u =
    entry.unit ||
    entry.order ||
    entry.attacker ||
    entry.drone ||
    entry.weapon ||
    entry.strategem ||
    entry.source;
  return u?.name || '';
}

function strategemLabel(entry) {
  return entry.strategem?.name || entry.order?.name || primaryEntityName(entry);
}

const amountNum = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : v);

/**
 * Generic server/client placeholder for deploy-effect (not the rich line from actionLog).
 */
function isGenericDeployEffectServerLine(msg) {
  const s = String(msg).trim();
  if (!s) return true;
  if (/\bused a deploy effect on\b/i.test(s)) return true;
  if (/\bused a deploy effect\s*$/i.test(s)) return true;
  if (/\bhat einen (?:Deploy|Einsatz)-Effekt auf\b/i.test(s)) return true;
  if (/\bhat einen (?:Deploy|Einsatz)-Effekt genutzt\s*$/i.test(s)) return true;
  if (/\busó un efecto de despliegue sobre\b/i.test(s)) return true;
  if (/\busó un efecto de despliegue\s*$/i.test(s)) return true;
  if (/\btargeted .+ with a deploy effect\b/i.test(s)) return true;
  if (/^[^:]+:\s*deploy effect\s*$/i.test(s)) return true;
  return false;
}

/**
 * @returns {string[] | null} null = use raw entry.message fallback (drawer only)
 */
function buildVerbatimLines(entry, t, nameCtx) {
  if (!entry || !entry.type) {
    return [];
  }

  const zoneLabel =
    entry.zone === 'front'
      ? t('battleUI.actionHistory.zoneFront')
      : entry.zone === 'flank'
        ? t('battleUI.actionHistory.zoneFlank')
        : entry.zone || '';

  switch (entry.type) {
    case 'unit-deployed': {
      const unitName = primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const withBlitz = entry.deployedWithBlitz === true;
      const lines = [];
      if (entry.zone === 'front') {
        lines.push(
          withBlitz
            ? t('battleUI.actionHistory.verbatimUnitDeployedFrontWithBlitz', { unitName })
            : t('battleUI.actionHistory.verbatimUnitDeployedFront', { unitName })
        );
      } else if (entry.zone === 'flank') {
        lines.push(
          withBlitz
            ? t('battleUI.actionHistory.verbatimUnitDeployedFlankWithBlitz', { unitName })
            : t('battleUI.actionHistory.verbatimUnitDeployedFlank', { unitName })
        );
      } else if (entry.zone && zoneLabel) {
        lines.push(
          withBlitz
            ? t('battleUI.actionHistory.verbatimUnitDeployedInWithBlitz', { unitName, zone: zoneLabel })
            : t('battleUI.actionHistory.verbatimUnitDeployedIn', { unitName, zone: zoneLabel })
        );
      } else {
        lines.push(
          withBlitz
            ? t('battleUI.actionHistory.verbatimUnitDeployedWithBlitz', { unitName })
            : t('battleUI.actionHistory.verbatimUnitDeployed', { unitName })
        );
      }
      if (entry.deploySnippet) {
        lines.push(
          t('battleUI.actionHistory.verbatimUnitDeployedCardLine', {
            defaultValue: '{{snippet}}',
            snippet: entry.deploySnippet
          })
        );
      }
      return lines;
    }
    case 'weapon-equipped': {
      const weaponName = entry.weapon?.name || primaryEntityName(entry) || t('battleUI.actionHistory.unknownWeapon', { defaultValue: 'Weapon' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const suffixParts = [];
      if (entry.attackDelta != null && Number(entry.attackDelta) !== 0) {
        suffixParts.push(t('battleUI.actionHistory.drawerPlusAttack', { n: entry.attackDelta }));
      }
      if (entry.healthDelta != null && Number(entry.healthDelta) !== 0) {
        suffixParts.push(t('battleUI.actionHistory.drawerPlusHealth', { n: entry.healthDelta }));
      }
      const granted = Array.isArray(entry.grantedAbilities) ? entry.grantedAbilities : [];
      if (granted.length > 0) {
        suffixParts.push(
          t('battleUI.actionHistory.verbatimWeaponGrantedAbilities', {
            abilities: granted.join(', ')
          })
        );
      }
      if (suffixParts.length > 0) {
        const suffix = suffixParts.join(
          ` ${t('battleUI.actionHistory.drawerStatJoin', { defaultValue: '·' })} `
        );
        return [
          t('battleUI.actionHistory.verbatimWeaponEquippedWithSuffix', {
            weaponName,
            targetName,
            suffix
          })
        ];
      }
      return [t('battleUI.actionHistory.verbatimWeaponEquipped', { weaponName, targetName })];
    }
    case 'weapon-stat-bonus': {
      const weaponName = entry.weapon?.name || t('battleUI.actionHistory.unknownWeapon', { defaultValue: 'Weapon' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const bits = [];
      if (entry.attackDelta != null && Number(entry.attackDelta) !== 0) {
        bits.push(t('battleUI.actionHistory.drawerPlusAttack', { n: entry.attackDelta }));
      }
      if (entry.healthDelta != null && Number(entry.healthDelta) !== 0) {
        bits.push(t('battleUI.actionHistory.drawerPlusHealth', { n: entry.healthDelta }));
      }
      const stats =
        bits.length > 0
          ? bits.join(` ${t('battleUI.actionHistory.drawerStatJoin', { defaultValue: '·' })} `)
          : '';
      return stats
        ? [t('battleUI.actionHistory.verbatimWeaponStatBonus', { weaponName, targetName, stats })]
        : [];
    }
    case 'drone-deployed': {
      const droneName = entry.drone?.name || primaryEntityName(entry) || t('battleUI.actionHistory.unknownDrone', { defaultValue: 'Drone' });
      return [t('battleUI.actionHistory.verbatimDroneDeployed', { droneName })];
    }
    case 'drone-charge-used': {
      const droneName = entry.drone?.name || primaryEntityName(entry) || t('battleUI.actionHistory.unknownDrone', { defaultValue: 'Drone' });
      if (entry.target?.name) {
        return [t('battleUI.actionHistory.verbatimDroneChargeOnTarget', { droneName, targetName: entry.target.name })];
      }
      return [t('battleUI.actionHistory.verbatimDroneCharge', { droneName })];
    }
    case 'drone-aura-applied': {
      const droneName = entry.drone?.name || t('battleUI.actionHistory.unknownDrone', { defaultValue: 'Drone' });
      return [
        t('battleUI.actionHistory.verbatimDroneAura', {
          droneName,
          attack: entry.attackDelta ?? 0,
          health: entry.healthDelta ?? 0
        })
      ];
    }
    case 'drone-exhausted-deploy': {
      const droneName = entry.drone?.name || t('battleUI.actionHistory.unknownDrone', { defaultValue: 'Drone' });
      const spawnedName = entry.spawnedUnit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const zoneWord = entry.zone && zoneLabel ? zoneLabel : '';
      return zoneWord
        ? [t('battleUI.actionHistory.verbatimDroneExhaustedInZone', { droneName, spawnedName, zone: zoneWord })]
        : [t('battleUI.actionHistory.verbatimDroneExhausted', { droneName, spawnedName })];
    }
    case 'ability-relay-grant': {
      const droneName = entry.drone?.name || t('battleUI.actionHistory.abilityRelayDrone', { defaultValue: 'Ability Relay Drone' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const ability = entry.grantedAbility || t('battleUI.actionHistory.unknownAbility', { defaultValue: 'ability' });
      return [
        t('battleUI.actionHistory.verbatimAbilityRelayGrant', { droneName, targetName, ability })
      ];
    }
    case 'unit-attacked': {
      const attackerName = entry.attacker?.name || primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const dmg = amountNum(entry.amount ?? entry.target?.damageTaken);
      const parts = [];
      if (dmg != null && dmg !== '') {
        parts.push(t('battleUI.actionHistory.verbatimAttackDealt', { attackerName, amount: dmg, targetName }));
      } else {
        parts.push(t('battleUI.actionHistory.verbatimAttackPlain', { attackerName, targetName }));
      }
      const dmgToAtt = amountNum(entry.damageToAttacker ?? entry.outcome?.damageToAttacker);
      if (dmgToAtt != null && Number(dmgToAtt) > 0) {
        parts.push(t('battleUI.actionHistory.verbatimRiposte', { attackerName, amount: dmgToAtt }));
      }
      if (entry.targetDestroyed) {
        parts.push(t('battleUI.actionHistory.verbatimTargetDestroyed', { targetName }));
      }
      if (entry.attackerDestroyed) {
        parts.push(t('battleUI.actionHistory.verbatimAttackerDestroyed', { attackerName }));
      }
      return parts;
    }
    case 'unit-attacked-general': {
      const attackerName = entry.attacker?.name || primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const tgtPid = entry.targetPlayerId ?? entry.outcome?.targetPlayerId;
      const isYourGeneral = nameCtx && tgtPid != null && String(tgtPid) === String(nameCtx.userId);
      const dmg = amountNum(entry.amount);
      const parts = [];
      if (dmg != null && dmg !== '') {
        parts.push(
          isYourGeneral
            ? t('battleUI.actionHistory.verbatimGeneralDamageYours', { attackerName, amount: dmg })
            : t('battleUI.actionHistory.verbatimGeneralDamageEnemy', { attackerName, amount: dmg })
        );
      } else {
        parts.push(
          isYourGeneral
            ? t('battleUI.actionHistory.verbatimGeneralAttackedYours', { attackerName })
            : t('battleUI.actionHistory.verbatimGeneralAttackedEnemy', { attackerName })
        );
      }
      const back = amountNum(entry.damageToAttacker);
      if (back != null && Number(back) > 0) {
        parts.push(t('battleUI.actionHistory.verbatimRiposte', { attackerName, amount: back }));
      }
      if (entry.attackerDestroyed) {
        parts.push(t('battleUI.actionHistory.verbatimAttackerDestroyed', { attackerName }));
      }
      return parts;
    }
    case 'barrier-lost': {
      const pid = entry.playerId;
      const isYou = nameCtx && pid != null && String(pid) === String(nameCtx.userId);
      if (isYou) {
        return [t('battleUI.actionHistory.verbatimBarrierLostYou')];
      }
      const playerName =
        resolveActionHistoryPlayerName(pid, nameCtx, t) ||
        t('battleUI.actionHistory.unknownPlayer', { defaultValue: 'Player' });
      return [t('battleUI.actionHistory.verbatimBarrierLostNamed', { playerName })];
    }
    case 'counter-strike-played': {
      const orderName =
        entry.order?.name ||
        entry.unit?.name ||
        entry.strategem?.name ||
        t('battleUI.actionHistory.unknownOrder', { defaultValue: 'Order' });
      const pid = entry.playerId;
      const isYou = nameCtx && pid != null && String(pid) === String(nameCtx.userId);
      const playerName =
        resolveActionHistoryPlayerName(pid, nameCtx, t) ||
        t('battleUI.actionHistory.unknownPlayer', { defaultValue: 'Player' });
      const targetName = entry.target?.name;
      const zoneWord =
        entry.zone === 'front'
          ? t('battleUI.actionHistory.zoneFront')
          : entry.zone === 'flank'
            ? t('battleUI.actionHistory.zoneFlank')
            : entry.zone || '';
      if (targetName) {
        return isYou
          ? [t('battleUI.actionHistory.verbatimCounterStrikeYouOn', { orderName, targetName })]
          : [t('battleUI.actionHistory.verbatimCounterStrikeNamedOn', { playerName, orderName, targetName })];
      }
      if (zoneWord) {
        return isYou
          ? [t('battleUI.actionHistory.verbatimCounterStrikeYouZone', { orderName, zone: zoneWord })]
          : [t('battleUI.actionHistory.verbatimCounterStrikeNamedZone', { playerName, orderName, zone: zoneWord })];
      }
      return isYou
        ? [t('battleUI.actionHistory.verbatimCounterStrikeYou', { orderName })]
        : [t('battleUI.actionHistory.verbatimCounterStrikeNamed', { playerName, orderName })];
    }
    case 'turn-passed': {
      const playerName = resolveActionHistoryPlayerName(entry.playerId, nameCtx, t) || t('battleUI.actionHistory.unknownPlayer', { defaultValue: 'Player' });
      return [t('battleUI.actionHistory.verbatimTurnPassed', { playerName })];
    }
    case 'turn-started': {
      const n = entry.turnNumber;
      return n != null
        ? [t('battleUI.actionHistory.verbatimTurnStarted', { turn: n })]
        : [t('battleUI.actionHistory.verbatimTurnStartedNoNumber', { defaultValue: 'New turn started' })];
    }
    case 'preparation-bonus-activated': {
      const playerName =
        resolveActionHistoryPlayerName(entry.playerId, nameCtx, t) ||
        t('battleUI.actionHistory.unknownPlayer', { defaultValue: 'Player' });
      if (entry.bonusType === 'black_market') {
        const gain = entry.intelGained != null ? entry.intelGained : 1;
        return [t('battleUI.actionHistory.verbatimBlackMarketDossier', { playerName, gain })];
      }
      if (entry.bonusType === 'plan_ahead') {
        return [t('battleUI.actionHistory.verbatimPlanAhead', { playerName })];
      }
      if (entry.bonusType === 'team_leader_meeting') {
        return [t('battleUI.actionHistory.verbatimTeamLeaderMeeting', { playerName })];
      }
      return [t('battleUI.actionHistory.verbatimPreparationBonus', { playerName, bonusType: entry.bonusType || 'bonus' })];
    }
    case 'strategem-requested': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      if (entry.target?.name) {
        return [t('battleUI.actionHistory.verbatimStrategemOn', { strategemName: sName, targetName: entry.target.name })];
      }
      return [t('battleUI.actionHistory.verbatimStrategem', { strategemName: sName })];
    }
    case 'strategem-damaged-unit': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const a = amountNum(entry.amount);
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      if (a != null && a !== '') {
        return [t('battleUI.actionHistory.verbatimStrategemDamageUnit', { strategemName: sName, amount: a, targetName })];
      }
      return [t('battleUI.actionHistory.verbatimStrategemAffectedUnit', { strategemName: sName, targetName })];
    }
    case 'strategem-damaged-general': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const a = amountNum(entry.amount);
      const tgtPid = entry.targetPlayerId;
      const isYours = nameCtx && tgtPid != null && String(tgtPid) === String(nameCtx.userId);
      if (a != null && a !== '') {
        return [
          isYours
            ? t('battleUI.actionHistory.verbatimStrategemDamageYourGeneral', { strategemName: sName, amount: a })
            : t('battleUI.actionHistory.verbatimStrategemDamageEnemyGeneral', { strategemName: sName, amount: a })
        ];
      }
      return [t('battleUI.actionHistory.verbatimStrategemAffectedGeneral', { strategemName: sName })];
    }
    case 'strategem-healed-general': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const a = amountNum(entry.amount);
      const tgtPid = entry.targetPlayerId;
      const isYours = nameCtx && tgtPid != null && String(tgtPid) === String(nameCtx.userId);
      if (a != null && a !== '') {
        return [
          isYours
            ? t('battleUI.actionHistory.verbatimStrategemHealYourGeneral', { strategemName: sName, amount: a })
            : t('battleUI.actionHistory.verbatimStrategemHealEnemyGeneral', { strategemName: sName, amount: a })
        ];
      }
      return [t('battleUI.actionHistory.verbatimStrategemAffectedGeneral', { strategemName: sName })];
    }
    case 'strategem-destroyed-unit': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [t('battleUI.actionHistory.verbatimStrategemDestroyed', { strategemName: sName, targetName })];
    }
    case 'strategem-spawned-unit': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const spawned = entry.spawnedUnit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [t('battleUI.actionHistory.verbatimStrategemSpawned', { strategemName: sName, targetName: spawned })];
    }
    case 'strategem-grew-unit':
    case 'strategem-shrank-unit': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [
        entry.type === 'strategem-grew-unit'
          ? t('battleUI.actionHistory.verbatimStrategemGrew', { strategemName: sName, targetName })
          : t('battleUI.actionHistory.verbatimStrategemShrank', { strategemName: sName, targetName })
      ];
    }
    case 'strategem-decommissioned-unit': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [t('battleUI.actionHistory.verbatimStrategemDecommissioned', { strategemName: sName, targetName })];
    }
    case 'strategem-multi-target': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const n = entry.targets?.length;
      if (n != null && n > 0) {
        return [t('battleUI.actionHistory.verbatimStrategemMulti', { strategemName: sName, count: n })];
      }
      return [t('battleUI.actionHistory.verbatimStrategemMultiFallback', { strategemName: sName })];
    }
    case 'strategem-hive-surge-buff': {
      const sName = strategemLabel(entry) || t('battleUI.actionHistory.unknownStrategem', { defaultValue: 'Strategem' });
      const atk = entry.attackDelta != null ? Number(entry.attackDelta) : 2;
      const names = (entry.targets || []).map((u) => u?.name).filter(Boolean);
      const unitList =
        names.length > 0
          ? names.join(', ')
          : t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [
        t('battleUI.actionHistory.verbatimHiveSurgeBuff', {
          strategemName: sName,
          attack: atk,
          unitList
        })
      ];
    }
    case 'confuse':
    case 'immobilize': {
      const actorName = primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [
        entry.type === 'confuse'
          ? t('battleUI.actionHistory.verbatimConfused', { actorName, targetName })
          : t('battleUI.actionHistory.verbatimImmobilized', { actorName, targetName })
      ];
    }
    case 'deploy-effect': {
      const serverDeployLine =
        (entry.effectDescription && String(entry.effectDescription).trim()) ||
        (entry.message && String(entry.message).trim());
      if (serverDeployLine && !isGenericDeployEffectServerLine(serverDeployLine)) {
        return [serverDeployLine];
      }

      const actorName = primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const targetName =
        entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });

      switch (entry.deployEffectKind) {
        case 'deploy_card_snippet': {
          const snippet = String(entry.deploySnippet || '');
          if (!snippet) {
            const fb = (entry.effectDescription && String(entry.effectDescription).trim()) ||
              (entry.message && String(entry.message).trim());
            if (fb) return [fb];
          }
          if (entry.target?.name) {
            return [
              t('battleUI.actionHistory.verbatimDeployCardSnippetOnTarget', {
                actorName,
                targetName,
                snippet
              })
            ];
          }
          return [t('battleUI.actionHistory.verbatimDeployCardSnippetSolo', { actorName, snippet })];
        }
        case 'confuse':
          return [t('battleUI.actionHistory.verbatimConfused', { actorName, targetName })];
        case 'immobilize':
          return [t('battleUI.actionHistory.verbatimImmobilized', { actorName, targetName })];
        case 'shield':
          return [t('battleUI.actionHistory.verbatimDeployShield', { actorName, targetName })];
        case 'destroy_drone':
          return [t('battleUI.actionHistory.verbatimDeployDestroyedDrone', { actorName, targetName })];
        case 'confuse_and_damage':
          return [t('battleUI.actionHistory.verbatimDeployConfuseOneDamage', { actorName, targetName })];
        case 'deploy_damage': {
          const amount =
            entry.deployDamageAmount != null ? Number(entry.deployDamageAmount) : 1;
          return [
            t('battleUI.actionHistory.verbatimDeployDamageTo', { actorName, targetName, amount })
          ];
        }
        case 'friendly_damage': {
          const amount =
            entry.deployDamageAmount != null ? Number(entry.deployDamageAmount) : 2;
          return [
            t('battleUI.actionHistory.verbatimDeployFriendlyDamage', { actorName, targetName, amount })
          ];
        }
        case 'confuse_immobilize_zone': {
          const zoneName = entry.deployZoneDisplay || entry.zone || '';
          return [
            t('battleUI.actionHistory.verbatimDeployConfuseImmobilizeZone', { actorName, zoneName })
          ];
        }
        case 'destroy_damaged':
          return [t('battleUI.actionHistory.verbatimDeployDestroyedDamaged', { actorName, targetName })];
        case 'shrink_stat': {
          const a = entry.deployGrowAttack != null ? Number(entry.deployGrowAttack) : 1;
          const h = entry.deployGrowHealth != null ? Number(entry.deployGrowHealth) : 1;
          return [t('battleUI.actionHistory.verbatimDeployShrinkBy', { actorName, targetName, attack: a, health: h })];
        }
        case 'destroy_low_attack': {
          const maxA = entry.deployMaxAttack != null ? Number(entry.deployMaxAttack) : 2;
          return [
            t('battleUI.actionHistory.verbatimDeployDestroyedLowAttack', { actorName, targetName, maxAttack: maxA })
          ];
        }
        case 'grow_grant_ability':
          return [
            t('battleUI.actionHistory.verbatimDeployGrewAndGrantedAbility', {
              actorName,
              targetName,
              attack: entry.deployGrowAttack ?? 0,
              health: entry.deployGrowHealth ?? 0,
              ability: entry.deployGrantedAbility || ''
            })
          ];
        case 'grow_stat':
          return [
            t('battleUI.actionHistory.verbatimDeployGrewStat', {
              actorName,
              targetName,
              attack: entry.deployGrowAttack ?? 0,
              health: entry.deployGrowHealth ?? 0
            })
          ];
        case 'double_stats':
          return [t('battleUI.actionHistory.verbatimDeployDoubledStats', { actorName, targetName })];
        case 'steal_weapons':
          return [t('battleUI.actionHistory.verbatimDeployStoleWeapons', { actorName, targetName })];
        case 'reposition':
          return [t('battleUI.actionHistory.verbatimDeployRepositioned', { actorName, targetName })];
        case 'abomination_target':
          return [t('battleUI.actionHistory.verbatimDeployAbomination', { actorName, targetName })];
        case 'plagueridden_grow':
          return [t('battleUI.actionHistory.verbatimDeployPlagueriddenGrow', { actorName, targetName })];
        case 'plagueridden_damage':
          return [t('battleUI.actionHistory.verbatimDeployPlagueriddenDamage', { actorName, targetName })];
        case 'plagueridden_ambiguous':
          return [t('battleUI.actionHistory.verbatimDeployPlagueriddenAmbiguous', { actorName, targetName })];
        case 'grant_ability':
          return [
            t('battleUI.actionHistory.verbatimDeployGrantedAbility', {
              actorName,
              targetName,
              ability: entry.deployGrantedAbility || ''
            })
          ];
        case 'decommission_grow':
          return [t('battleUI.actionHistory.verbatimDeployDecommissionGrow', { actorName, targetName })];
        default:
          break;
      }

      if (entry.effectDescription) {
        return [String(entry.effectDescription)];
      }
      if (entry.unit?.id === 309) {
        return [t('battleUI.actionHistory.verbatimDeployShield', { actorName, targetName })];
      }
      if (entry.target?.name) {
        return [t('battleUI.actionHistory.verbatimDeployEffect', { actorName, targetName: entry.target.name })];
      }
      return [t('battleUI.actionHistory.verbatimDeployEffectDefault', { actorName })];
    }
    case 'unit-redeem-damage': {
      const actorName = primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const a = amountNum(entry.amount);
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      if (a != null && a !== '') {
        if (entry.targetDestroyed) {
          return [t('battleUI.actionHistory.verbatimRedeemDamageDestroyed', { actorName, amount: a, targetName })];
        }
        return [t('battleUI.actionHistory.verbatimRedeemDamage', { actorName, amount: a, targetName })];
      }
      return [t('battleUI.actionHistory.verbatimRedeemDamageFallback', { actorName, targetName })];
    }
    case 'unit-special-effect-damage': {
      const actorName = primaryEntityName(entry) || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const a = amountNum(entry.amount);
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      if (a != null && a !== '') {
        return [t('battleUI.actionHistory.verbatimSpecialDamage', { actorName, amount: a, targetName })];
      }
      return [t('battleUI.actionHistory.verbatimSpecialAffected', { actorName, targetName })];
    }
    case 'drone-damage':
      return entry.description
        ? [String(entry.description)]
        : [t('battleUI.actionHistory.tooltipDroneDamageFallback')];
    case 'drone-non-damage':
      return entry.effectType && entry.target?.name
        ? [t('battleUI.actionHistory.verbatimDroneEffect', { effect: String(entry.effectType), targetName: entry.target.name })]
        : [t('battleUI.actionHistory.tooltipDroneDamageFallback')];
    case 'eu-boss-weapons-randomizer-fired': {
      const unitName =
        typeof entry.targetUnitName === 'string' && entry.targetUnitName.length > 0
          ? entry.targetUnitName
          : entry.target?.name || t('battleUI.actionHistory.target');
      return [t('battleUI.actionHistory.weaponRandomizerTargeted', { unitName })];
    }
    case 'eu-boss-weapons-randomizer-skipped':
      return [t('battleUI.actionHistory.weaponRandomizerSkipped')];
    case 'eu-creation-fireball-fired': {
      if (entry.targetKind === 'general') {
        return [
          t('battleUI.actionHistory.creationFireballGeneral', {
            damage: entry.damage ?? 0,
          }),
        ];
      }
      const unitName =
        typeof entry.targetUnitName === 'string' && entry.targetUnitName.length > 0
          ? entry.targetUnitName
          : t('battleUI.actionHistory.target');
      return [
        t('battleUI.actionHistory.creationFireballUnit', {
          unitName,
          damage: entry.damage ?? 0,
        }),
      ];
    }
    case 'eu-creation-aflame-tick': {
      if (entry.targetKind === 'general') {
        return [t('battleUI.actionHistory.creationAflameTickGeneral', { damage: entry.damage ?? 0 })];
      }
      const unitName =
        typeof entry.targetUnitName === 'string' && entry.targetUnitName.length > 0
          ? entry.targetUnitName
          : t('battleUI.actionHistory.target');
      return [
        t('battleUI.actionHistory.creationAflameTickUnit', {
          unitName,
          damage: entry.damage ?? 0,
        }),
      ];
    }
    case 'penetrate-excess-general': {
      const attackerName = entry.attacker?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const viaUnit = entry.target?.name || '';
      const excess = entry.excessDamage;
      return [
        t('battleUI.actionHistory.verbatimPenetrateExcess', {
          attackerName,
          excess,
          viaUnit: viaUnit || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' })
        })
      ];
    }
    case 'attack-effect-general-damage': {
      const attackerName = entry.attacker?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const viaUnit = entry.target?.name || '';
      const dmg = amountNum(entry.amount);
      const tgtPid = entry.generalPlayerId;
      const isYourGeneral = nameCtx && tgtPid != null && String(tgtPid) === String(nameCtx.userId);
      if (viaUnit && dmg != null && dmg !== '') {
        return [
          isYourGeneral
            ? t('battleUI.actionHistory.verbatimAttackEffectGeneralYoursVia', { attackerName, amount: dmg, viaUnit })
            : t('battleUI.actionHistory.verbatimAttackEffectGeneralEnemyVia', { attackerName, amount: dmg, viaUnit })
        ];
      }
      if (dmg != null && dmg !== '') {
        return [
          isYourGeneral
            ? t('battleUI.actionHistory.verbatimAttackEffectGeneralYours', { attackerName, amount: dmg })
            : t('battleUI.actionHistory.verbatimAttackEffectGeneralEnemy', { attackerName, amount: dmg })
        ];
      }
      return [
        isYourGeneral
          ? t('battleUI.actionHistory.verbatimAttackEffectGeneralYoursFallback', { attackerName })
          : t('battleUI.actionHistory.verbatimAttackEffectGeneralEnemyFallback', { attackerName })
      ];
    }
    case 'recover-healed-general': {
      const unitName = entry.source?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const heal = amountNum(entry.amount);
      const tgtPid = entry.generalPlayerId;
      const isYourGeneral = nameCtx && tgtPid != null && String(tgtPid) === String(nameCtx.userId);
      if (heal != null && heal !== '') {
        return [
          isYourGeneral
            ? t('battleUI.actionHistory.verbatimRecoverHealYourGeneral', { unitName, amount: heal })
            : t('battleUI.actionHistory.verbatimRecoverHealEnemyGeneral', { unitName, amount: heal })
        ];
      }
      return [
        isYourGeneral
          ? t('battleUI.actionHistory.verbatimRecoverHealYourGeneral', { unitName, amount: '?' })
          : t('battleUI.actionHistory.verbatimRecoverHealEnemyGeneral', { unitName, amount: '?' })
      ];
    }
    case 'ability-blitz': {
      const unitName = entry.unit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      if (entry.grantKind === 'deploy-native') {
        return [t('battleUI.actionHistory.verbatimBlitzDeploy', { unitName })];
      }
      const sourceName = entry.source?.name;
      return sourceName
        ? [t('battleUI.actionHistory.verbatimBlitzGrantedFrom', { unitName, sourceName })]
        : [t('battleUI.actionHistory.verbatimBlitzGranted', { unitName })];
    }
    case 'ability-sniper-granted': {
      const unitName = entry.unit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const sourceName = entry.source?.name;
      return sourceName
        ? [t('battleUI.actionHistory.verbatimSniperGrantedFrom', { unitName, sourceName })]
        : [t('battleUI.actionHistory.verbatimSniperGranted', { unitName })];
    }
    case 'ability-sniper-kill': {
      const attackerName = entry.attacker?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [t('battleUI.actionHistory.verbatimSniperKill', { attackerName, targetName })];
    }
    case 'ability-shield-absorbed': {
      const unitName = entry.unit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const amt = amountNum(entry.amount);
      return [t('battleUI.actionHistory.verbatimShieldAbsorbed', { unitName, amount: amt ?? '?' })];
    }
    case 'ability-raid': {
      const unitName = entry.unit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const reward = entry.raidReward || t('battleUI.actionHistory.raidRewardFallback', { defaultValue: 'Raid bonus' });
      return [t('battleUI.actionHistory.verbatimRaid', { unitName, reward })];
    }
    case 'ability-mend-wounds': {
      const unitName = entry.unit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      return [t('battleUI.actionHistory.verbatimMendWounds', { unitName })];
    }
    case 'fell-intel': {
      const attackerName = entry.attacker?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const targetName = entry.target?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const n = entry.intelGained != null ? entry.intelGained : 1;
      return [t('battleUI.actionHistory.verbatimFellIntel', { attackerName, targetName, intel: n })];
    }
    case 'fell-summon': {
      const attackerName = entry.attacker?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const summonedName = entry.summonedUnit?.name || t('battleUI.actionHistory.unknownUnit', { defaultValue: 'Unknown unit' });
      const killedName = entry.killedUnit?.name;
      return killedName
        ? [t('battleUI.actionHistory.verbatimFellSummonReplace', { attackerName, killedName, summonedName })]
        : [t('battleUI.actionHistory.verbatimFellSummon', { attackerName, summonedName })];
    }
    case 'order-received-hand': {
      const isSelf =
        nameCtx &&
        entry.playerId != null &&
        String(entry.playerId) === String(nameCtx.userId);
      const viaFleshMimic =
        entry.handReceiveKind === 'flesh_mimic' || entry.source === 'flesh_mimic';
      if (!isSelf) {
        if (viaFleshMimic) {
          return [
            t('battleUI.actionHistory.verbatimOpponentFleshMimic', {
              defaultValue: 'Copied an order with Flesh Mimic'
            })
          ];
        }
        return [t('battleUI.actionHistory.verbatimOpponentReceivedOrderHidden')];
      }
      const orderName =
        entry.order?.name ||
        entry.unit?.name ||
        t('battleUI.actionHistory.unknownOrder', { defaultValue: 'Order' });
      if (viaFleshMimic) {
        return [
          t('battleUI.actionHistory.verbatimFleshMimicDrew', {
            orderName,
            defaultValue: 'Flesh Mimic drew {{orderName}}'
          })
        ];
      }
      return [t('battleUI.actionHistory.verbatimOrderReceived', { orderName })];
    }
    default:
      return null;
  }
}

/**
 * @param {object} entry
 * @param {import('i18next').TFunction} t
 * @param {{ userId?: *, opponentId?: *, currentUsername?: string, opponentUsername?: string } | null} [nameCtx]
 */
export function buildBattleActionHistoryTooltipLines(entry, t, nameCtx = null) {
  const v = buildVerbatimLines(entry, t, nameCtx);
  if (v !== null) {
    return v;
  }
  return [];
}

/**
 * @param {object} entry
 * @param {import('i18next').TFunction} t
 * @param {{ userId?: *, opponentId?: *, currentUsername?: string, opponentUsername?: string } | null} [nameCtx]
 * @returns {string[]}
 */
export function buildBattleActionHistoryDrawerLines(entry, t, nameCtx = null) {
  const v = buildVerbatimLines(entry, t, nameCtx);
  if (v !== null) {
    if (v.length > 0) return v;
    if (entry?.message) return [String(entry.message)];
    return [];
  }
  if (entry?.message) {
    return [String(entry.message)];
  }
  const primary = primaryEntityName(entry);
  return primary ? [`${primary} — ${entry.type}`] : [String(entry.type || '')];
}

/**
 * Collect card / unit / drone / weapon names from a server actionLog entry or a hydrated ActionHistory action.
 * Used to highlight those substrings inside log prose (drawer, tooltips).
 * @param {object} obj
 * @returns {string[]}
 */
export function collectEntityNamesFromLogPayload(obj) {
  if (!obj || typeof obj !== 'object') return [];
  const set = new Set();
  const add = (n) => {
    if (n == null) return;
    const s = String(n).trim();
    if (s.length > 0) set.add(s);
  };
  add(obj.unit?.name);
  add(obj.order?.name);
  add(obj.attacker?.name);
  add(obj.target?.name);
  add(obj.drone?.name);
  add(obj.weapon?.name);
  add(obj.strategem?.name);
  add(obj.source?.name);
  add(obj.killer?.name);
  add(obj.spawnedUnit?.name);
  add(obj.summonedUnit?.name);
  add(obj.killedUnit?.name);
  add(obj.actionItem?.name);
  add(obj.enhancer?.name);
  if (Array.isArray(obj.targets)) {
    for (const u of obj.targets) {
      add(u?.name);
    }
  }
  return Array.from(set).sort((a, b) => b.length - a.length);
}

/**
 * Map trimmed display name → faction accent hex for entities present on this log payload.
 * Names without a resolvable faction are omitted (UI falls back to muted gray).
 * @param {object} obj
 * @returns {Record<string, string>}
 */
export function collectEntityFactionColorByNameFromLogPayload(obj) {
  if (!obj || typeof obj !== 'object') return {};
  /** @type {Record<string, string>} */
  const map = Object.create(null);
  const assign = (entity) => {
    if (!entity) return;
    const n = entity.name;
    if (n == null) return;
    const key = String(n).trim();
    if (!key) return;
    const hex = getFactionAccentColorForLogEntity(entity);
    if (hex) map[key] = hex;
  };
  assign(obj.unit);
  assign(obj.order);
  assign(obj.attacker);
  assign(obj.target);
  assign(obj.drone);
  assign(obj.weapon);
  assign(obj.strategem);
  assign(obj.source);
  assign(obj.killer);
  assign(obj.spawnedUnit);
  assign(obj.summonedUnit);
  assign(obj.killedUnit);
  assign(obj.actionItem);
  assign(obj.enhancer);
  if (Array.isArray(obj.targets)) {
    for (const u of obj.targets) {
      assign(u);
    }
  }
  return map;
}

/**
 * Split a line into plain text vs unit-name spans (longest names first).
 * @param {string} line
 * @param {string[]} names from {@link collectEntityNamesFromLogPayload}
 * @param {Record<string, string> | null | undefined} [nameToColor] from {@link collectEntityFactionColorByNameFromLogPayload}
 * @returns {{ text: string, isUnit: boolean, color?: string | null }[]}
 */
export function splitLineForUnitHighlights(line, names, nameToColor) {
  const s = String(line ?? '');
  const sorted = [...new Set((names || []).filter(Boolean).map((n) => String(n).trim()))].sort(
    (a, b) => b.length - a.length
  );
  if (!s || sorted.length === 0) {
    return [{ text: s, isUnit: false }];
  }

  const out = [];
  let i = 0;
  while (i < s.length) {
    let matched = null;
    for (const name of sorted) {
      if (name.length < 2) continue;
      if (s.slice(i, i + name.length) !== name) continue;
      const before = i > 0 ? s[i - 1] : ' ';
      const after = i + name.length < s.length ? s[i + name.length] : ' ';
      if (!name.includes(' ') && /\w/.test(before) && /\w/.test(after)) {
        continue;
      }
      matched = name;
      break;
    }
    if (matched) {
      const hex = nameToColor && typeof nameToColor === 'object' ? nameToColor[matched] : undefined;
      out.push({ text: matched, isUnit: true, color: hex || null });
      i += matched.length;
    } else {
      out.push({ text: s[i], isUnit: false });
      i += 1;
    }
  }

  const merged = [];
  for (const seg of out) {
    const prev = merged[merged.length - 1];
    if (prev && !prev.isUnit && !seg.isUnit) {
      prev.text += seg.text;
    } else {
      merged.push({
        text: seg.text,
        isUnit: seg.isUnit,
        ...(seg.isUnit ? { color: seg.color ?? null } : {}),
      });
    }
  }
  return merged;
}
